import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Form from 'antd/es/form/index'
import Input from 'antd/es/input'
import Select from 'antd/es/select/index'
import Radio from 'antd/es/radio/index'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Spin from 'antd/es/spin/index'
import Tooltip from 'antd/es/tooltip/index'
import { InfoCircleOutlined } from '@ant-design/icons'
import { convertPxToNumber } from '@/utils/helpers.ts'
import IdeyaModal from '@/common/antd/ideya-modal.tsx'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button.tsx'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import { useAppSelector } from '@/redux/hooks.ts'
import { selectThemeStyle } from '@/features/setting/setting.slice.ts'
import { useTranslation } from 'react-i18next'
import { useListUserOptionsQuery } from '@/apis/option/option-api.api.ts'
import { useLazyListMailEntriesQuery } from '@/apis/mail-list/mail-list-api.api.ts'
import { MailListEntry } from '@/apis/mail-list/mail-list-api.def.ts'
import { useListUsersQuery } from '@/apis/user-management/user-management-api.api.ts'
import { AccountType } from '@/enums/management-input.enum.ts'
import { MailListType } from '@/enums/mail-list.enum.ts'

// Whether the user is chosen from the existing DB list or typed as a custom email.
type MailSourceType = 'user' | 'custom'

// Delay before checking a typed custom email against existing entries.
const EXISTING_EMAIL_CHECK_DELAY_MS = 500
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface MailListModalFieldTypes {
  sourceType: MailSourceType
  userId?: string
  email?: string
  // White and Block membership are mutually exclusive, so the modal picks one.
  listType?: MailListType
}

// Payload emitted on submit. Provides either a userId (existing user) or a
// custom email, plus the selected list. The page maps this to the add or edit
// mutation depending on the modal mode.
export interface MailListModalSubmit {
  userId?: string
  email?: string
  listType: MailListType
}

// Prefilled state for the edit flow.
export interface MailListEditTarget {
  id: string
  email: string
  userId?: string
  fullName?: string
  listType: MailListType
}

interface MailListModalProps {
  open: boolean
  mode: 'create' | 'edit'
  editTarget?: MailListEditTarget | null
  isSubmitting: boolean
  onCancel: () => void
  onSubmit: (input: MailListModalSubmit) => void
  onRemove: () => void
  // Called in add mode when the chosen email already belongs to a list, so the
  // page can switch this modal into edit mode for the existing entry.
  onExistingEntry: (entry: MailListEntry) => void
}

export const MailListModal: React.FC<MailListModalProps> = ({
  open,
  mode,
  editTarget,
  isSubmitting,
  onCancel,
  onSubmit,
  onRemove,
  onExistingEntry,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const [form] = Form.useForm<MailListModalFieldTypes>()
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)

  const sourceType = Form.useWatch('sourceType', form)

  const { data: userOptionData, isLoading: isLoadingUserOptions } =
    useListUserOptionsQuery({})

  // The option endpoint only returns name + id, so pull emails from the user
  // list (id matches the option value) to show and filter by email as well.
  const { data: externalUsers } = useListUsersQuery({
    page: 1,
    pageSize: 10000,
    accountType: AccountType.External,
  })
  const { data: internalUsers } = useListUsersQuery({
    page: 1,
    pageSize: 10000,
    accountType: AccountType.Internal,
  })

  const emailByUserId = useMemo(() => {
    const map = new Map<string, string>()
    for (const user of [
      ...(externalUsers?.data.users ?? []),
      ...(internalUsers?.data.users ?? []),
    ]) {
      if (user.email) map.set(user.id, user.email)
    }
    return map
  }, [externalUsers, internalUsers])

  // In add mode, look up whether the chosen email already sits in either list;
  // if so, hand the existing entry to the page to continue as an edit.
  const [lookupMailEntries] = useLazyListMailEntriesQuery()
  // Latest email being looked up; responses for any other email are stale.
  const latestLookupRef = useRef<string | null>(null)
  const emailCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearEmailCheckTimer = useCallback(() => {
    if (emailCheckTimerRef.current) {
      clearTimeout(emailCheckTimerRef.current)
      emailCheckTimerRef.current = null
    }
  }, [])

  const checkExistingEntry = useCallback(
    async (email?: string) => {
      const target = email?.trim().toLowerCase()
      if (!target) return
      latestLookupRef.current = target
      try {
        const res = await lookupMailEntries({
          page: 1,
          pageSize: 20,
          name: target,
        }).unwrap()
        if (latestLookupRef.current !== target) return
        const match = res.data.entries.find(
          (entry) => entry.email.toLowerCase() === target
        )
        if (match) onExistingEntry(match)
      } catch {
        // Lookup failure shouldn't block adding; the shared base query notifies.
      }
    },
    [lookupMailEntries, onExistingEntry]
  )

  const scheduleEmailCheck = (value: string) => {
    clearEmailCheckTimer()
    const trimmed = value.trim()
    if (!EMAIL_PATTERN.test(trimmed)) {
      latestLookupRef.current = null
      return
    }
    emailCheckTimerRef.current = setTimeout(() => {
      emailCheckTimerRef.current = null
      void checkExistingEntry(trimmed)
    }, EXISTING_EMAIL_CHECK_DELAY_MS)
  }

  // Drop pending checks and late responses when the modal closes or switches
  // mode, so a stale lookup can't flip a fresh modal into edit mode.
  useEffect(() => {
    return () => {
      clearEmailCheckTimer()
      latestLookupRef.current = null
    }
  }, [open, mode, clearEmailCheckTimer])

  const fieldValues = useMemo<MailListModalFieldTypes>(
    () =>
      mode === 'edit' && editTarget
        ? {
            sourceType: editTarget.userId ? 'user' : 'custom',
            userId: editTarget.userId,
            email: editTarget.email,
            listType: editTarget.listType,
          }
        : {
            sourceType: 'user',
            userId: undefined,
            email: undefined,
            listType: undefined,
          },
    [mode, editTarget]
  )

  // destroyOnHidden remounts the Form on every open, after this effect has
  // already run, so the first render relies on initialValues. The effect only
  // covers switching to edit mode while the modal stays open.
  useEffect(() => {
    if (!open) return
    setShowRemoveConfirm(false)
    form.setFieldsValue(fieldValues)
  }, [open, fieldValues, form])

  const handleOk = async () => {
    let values: MailListModalFieldTypes
    try {
      values = await form.validateFields()
    } catch {
      // antd renders the field errors; swallow the validation rejection.
      return
    }

    // validateFields guarantees listType via the required rule.
    const membership = { listType: values.listType as MailListType }

    if (values.sourceType === 'user') {
      onSubmit({ userId: values.userId, ...membership })
    } else {
      // In edit mode the email input isn't rendered, so fall back to the
      // target's email; the page ignores this field for edits regardless.
      onSubmit({
        email: (values.email ?? editTarget?.email ?? '').trim(),
        ...membership,
      })
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  const userOptions = useMemo(
    () =>
      userOptionData?.data.map((user) => {
        const value = String(user.value)
        const email = emailByUserId.get(value)
        return {
          value,
          label: email ? `${user.label} · ${email}` : user.label,
        }
      }) ?? [],
    [userOptionData, emailByUserId]
  )

  // Human-readable label for the linked user in edit mode: prefer the option
  // label, fall back to the entry's name/email so the admin never sees a raw id.
  const editUserDisplay = useMemo(() => {
    if (!editTarget) return ''
    const matched = userOptions.find(
      (option) => option.value === editTarget.userId
    )
    if (matched) return matched.label
    if (editTarget.fullName) {
      return `${editTarget.fullName} · ${editTarget.email}`
    }
    return editTarget.email
  }, [editTarget, userOptions])

  return (
    <IdeyaModal
      open={open}
      title={
        mode === 'edit'
          ? t('mail_list.modal.edit_title')
          : t('mail_list.modal.add_title')
      }
      destroyOnHidden
      isForm={true}
      onCancel={handleCancel}
    >
      <Form form={form} layout={'vertical'} initialValues={fieldValues}>
        <Form.Item label={t('mail_list.modal.source_type')} name={'sourceType'}>
          <Radio.Group disabled={mode === 'edit'}>
            <Radio value={'user'}>
              <IdeyaTypography.Text>
                {t('mail_list.modal.existing_user')}
              </IdeyaTypography.Text>
            </Radio>
            <Radio value={'custom'}>
              <IdeyaTypography.Text>
                {t('mail_list.modal.custom_email')}
              </IdeyaTypography.Text>
            </Radio>
          </Radio.Group>
        </Form.Item>

        {mode === 'edit' ? (
          <Form.Item
            label={
              editTarget?.userId
                ? t('mail_list.modal.select_user')
                : t('mail_list.modal.email')
            }
          >
            {isLoadingUserOptions ? (
              <Spin />
            ) : (
              <IdeyaTypography.Text>{editUserDisplay}</IdeyaTypography.Text>
            )}
          </Form.Item>
        ) : sourceType === 'user' ? (
          <Form.Item
            label={t('mail_list.modal.select_user')}
            name={'userId'}
            rules={[
              {
                required: true,
                message: t('mail_list.modal.please_select_user'),
              },
            ]}
          >
            {isLoadingUserOptions ? (
              <Spin />
            ) : (
              <Select
                showSearch
                options={userOptions}
                placeholder={t('mail_list.modal.select_user')}
                onChange={(value) =>
                  checkExistingEntry(emailByUserId.get(String(value)))
                }
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            )}
          </Form.Item>
        ) : (
          <Form.Item
            label={t('mail_list.modal.email')}
            name={'email'}
            rules={[
              {
                required: true,
                message: t('mail_list.modal.please_input_email'),
              },
              {
                type: 'email',
                message: t('mail_list.modal.please_enter_valid_email'),
              },
            ]}
          >
            <Input
              onChange={(e) => scheduleEmailCheck(e.target.value)}
              onBlur={(e) => {
                clearEmailCheckTimer()
                const value = e.target.value.trim().toLowerCase()
                // Skip if the debounced check already looked up this email.
                if (value && value !== latestLookupRef.current) {
                  void checkExistingEntry(value)
                }
              }}
            />
          </Form.Item>
        )}

        <Form.Item label={t('mail_list.modal.lists')} required>
          {/* The noStyle child's rule error is rendered by this parent item. */}
          <Form.Item
            name={'listType'}
            noStyle
            rules={[
              {
                required: true,
                message: t('mail_list.modal.please_select_a_list'),
              },
            ]}
          >
            <Radio.Group>
              <Row gutter={[convertPxToNumber(themeStyle.marginBase), 0]}>
                <Col>
                  <Radio value={MailListType.Block}>
                    <IdeyaTypography.Text>
                      {t('mail_list.controls.block_list')}
                    </IdeyaTypography.Text>
                  </Radio>
                  <Tooltip title={t('mail_list.modal.block_list_tooltip')}>
                    <InfoCircleOutlined
                      style={{ marginLeft: themeStyle.marginXXS }}
                    />
                  </Tooltip>
                </Col>
                <Col>
                  <Radio value={MailListType.White}>
                    <IdeyaTypography.Text>
                      {t('mail_list.controls.white_list')}
                    </IdeyaTypography.Text>
                  </Radio>
                  <Tooltip title={t('mail_list.modal.white_list_tooltip')}>
                    <InfoCircleOutlined
                      style={{ marginLeft: themeStyle.marginXXS }}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Radio.Group>
          </Form.Item>
        </Form.Item>

        <Row
          style={{
            display: 'flex',
            justifyContent: mode === 'edit' ? 'space-between' : 'flex-end',
            gap: themeStyle.marginXS,
            marginTop: themeStyle.marginXXL,
          }}
        >
          {mode === 'edit' && (
            <IdeyaButton
              danger
              ghost
              onClick={() => setShowRemoveConfirm(true)}
              disabled={isSubmitting}
            >
              {t('mail_list.modal.remove')}
            </IdeyaButton>
          )}
          <Row style={{ display: 'flex', gap: themeStyle.marginXS }}>
            <IdeyaButton ghost onClick={handleCancel} disabled={isSubmitting}>
              {t('common.cancel')}
            </IdeyaButton>
            <IdeyaButton onClick={handleOk} loading={isSubmitting}>
              {t('common.ok')}
            </IdeyaButton>
          </Row>
        </Row>
      </Form>

      <IdeyaModal
        open={showRemoveConfirm}
        title={t('mail_list.modal.remove_confirm_title')}
        destroyOnHidden
        onOk={() => {
          setShowRemoveConfirm(false)
          onRemove()
        }}
        onCancel={() => setShowRemoveConfirm(false)}
      >
        <IdeyaTypography.Text>
          {t('mail_list.modal.remove_confirm_message')}
        </IdeyaTypography.Text>
      </IdeyaModal>
    </IdeyaModal>
  )
}
